import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { ISelectOption } from '@core/interfaces/select-option.interface';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { TableStateService } from '@core/services/table-state.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatatableBaseComponent } from '@shared/components/datatable-base/datatable-base.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDataRefreshRequestParams,
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { isAfter } from 'date-fns';
import { snakeCase } from 'lodash';
import { combineLatest, distinctUntilChanged, filter } from 'rxjs';
import { IAdminUserInvitation, TAdminInvitationRole } from '../admin-user-group.interface';
import { AdminInvitationDialogComponent } from './admin-invitation-dialog/admin-invitation-dialog.component';
import { IAdminInvitationDialogData } from './admin-invitation-dialog/admin-invitation-dialog.interface';
import { AdminInvitationsTabStore } from './admin-invitations-tab.store';

@Component({
  imports: [
    DatatableComponent,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOption,
    TranslateModule,
    DatePipe,
    MatTooltipModule,
    HasPermissionDirective,
  ],
  providers: [AdminInvitationsTabStore],
  selector: 'app-admin-invitations-tab',
  styleUrl: './admin-invitations-tab.component.scss',
  templateUrl: './admin-invitations-tab.component.html',
})
export class AdminInvitationsTabComponent extends DatatableBaseComponent implements OnInit {
  public readonly userGroups = input.required<IUserGroup[] | null>();

  public readonly refreshUserGroups = output<void>();

  public readonly dataSource = signal<MatTableDataSource<IAdminUserInvitation>>(
    new MatTableDataSource<IAdminUserInvitation>([]),
  );
  public readonly loadedTableData = signal<boolean>(false);
  public readonly selectedRole = signal<TAdminInvitationRole | null>(null);
  public readonly emailNotSent = signal<boolean>(false);

  public readonly canInvite = computed(() => {
    return this.selectedRole() === this.ERoles.campaign || this.selectedRole() === this.ERoles.support;
  });

  public datatableColumns: TDatatableColumn<IAdminUserInvitation, string>[] = [
    { dataViewStrategy: () => '', id: 'status' },
    { id: 'userGroupId' },
    { id: 'email' },
    { dataViewStrategy: (row) => row.user?.name ?? '-', id: 'createdByUserName', isSortable: true },
    { id: 'createdAt' },
  ];

  public datatableConfig!: IDatatableConfiguration<IAdminUserInvitation>;

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        circleSize: '16px',
        className: 'w-[10%]',
        columnPadding: '0 8px',
        hasText: true,
        rowPadding: '18px 0',
        textWidth: '100px',
        type: 'icon',
      },
      { borderRadius: '4px', className: 'w-[24%]', textWidth: '120px', type: 'text' },
      { borderRadius: '4px', className: 'w-[34%]', textWidth: '200px', type: 'text' },
      { borderRadius: '4px', className: 'w-[16%]', textWidth: '150px', type: 'text' },
      { borderRadius: '4px', className: 'w-[16%]', textWidth: '150px', type: 'text' },
    ],
    filters: [{ inputClass: '!min-w-[220px]' }],
    rows: 5,
    showFilters: true,
    showHeaderControls: true,
    showSearchField: true,
  };

  public readonly store = inject(AdminInvitationsTabStore);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly tableStateService: TableStateService = inject(TableStateService);

  public readonly roleOptions: ISelectOption<TAdminInvitationRole>[] = [
    {
      label: this.translate.instant('pages.admin.userGroup.tabs.invitations.roles.support'),
      value: this.ERoles.support,
    },
    {
      label: this.translate.instant('pages.admin.userGroup.tabs.invitations.roles.campaign'),
      value: this.ERoles.campaign,
    },
    {
      label: this.translate.instant('pages.admin.userGroup.tabs.invitations.roles.campaignSub'),
      value: this.ERoles.campaignSub,
    },
    {
      label: this.translate.instant('pages.admin.userGroup.tabs.invitations.roles.creator'),
      value: this.ERoles.creator,
    },
  ];

  constructor() {
    super();

    effect(() => {
      const data = this.store.data$();
      const role = this.selectedRole();

      if (role && data && (data.length > 0 || !this.store.dataLoading$())) {
        const mappedData = data.map((invitation: IAdminUserInvitation) => ({
          ...invitation,
          isExpired: invitation.expire && isAfter(new Date(), this.dateUtils.parseDateTimeFromApi(invitation.expire)),
          role,
          // userGroupName: this.userGroups().find((ug) => ug.id === invitation.userGroupId)?.name,
        }));
        this.dataSource().data = mappedData;
        this.loadedTableData.set(true);
      }
    });

    effect(() => {
      this.userGroups();

      this.setDatatableConfig();
    });

    effect(() => {
      if (this.emailNotSent()) {
        this.refreshUserGroups.emit();
        this.store.fetchInvitations({ params: this.prepareFetchParams(this.datatableParams$.value!) });
      }
    });
  }

  public ngOnInit(): void {
    this.setDatatableConfig();
  }

  public onRoleChange(role: TAdminInvitationRole): void {
    this.selectedRole.set(role);
    this.loadedTableData.set(false);
    this.store.patchState({ data: null });

    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      })
        .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
        .subscribe(({ datatable }) => {
          this.store.fetchInvitations({ params: this.prepareFetchParams(datatable) });
        }),
    );
  }

  public onInviteClick(): void {
    if (!this.canInvite()) {
      return;
    }

    this.openInvitationDialog(null);
  }

  public onRowClick(row: IRowClickEvent<IAdminUserInvitation, string>): void {
    this.openInvitationDialog(row);
  }

  private prepareFetchParams(params: IDataRefreshRequestParams): {
    filters?: Record<string, unknown>;
    joins?: string[];
    limit: number;
    page: number;
    search?: { searchedFields: string[]; searchText: string };
    sort: Sort[];
  } {
    return {
      // eslint-disable-next-line
      filters: { ...(params.filters ?? {}), target_role: snakeCase(this.selectedRole()!) },
      joins: ['user'],
      limit: params.limit,
      page: params.page,
      sort: [params.sort.active === 'createdByUserName' ? { ...params.sort, active: 'created_by' } : params.sort],
      ...(params.search ? { search: { searchedFields: ['email__ilike'], searchText: params.search } } : {}),
    };
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      filters: [
        {
          key: 'user_group_id__in',
          label: `${this.translate.instant('pages.admin.userGroup.userGroup')}`,
          multiple: true,
          options:
            this.userGroups()?.map((ug) => ({
              label: ug.name,
              value: ug.id,
            })) ?? [],
          placeholder: `${this.translate.instant('shared.all')}`,
        },
      ],
      isClientSide: false,
      skipInitialRequest: false,
      storageKey: this.tableStateService.buildKey('admin-user-group', 'invitations-tab'),
    } satisfies IDatatableConfiguration<IAdminUserInvitation>;
  }

  private openInvitationDialog(row: IRowClickEvent<IAdminUserInvitation, string> | null): void {
    this.emailNotSent.set(false);

    const data: IAdminInvitationDialogData = {
      ...(row ? { invitation: row.row } : {}),
      emailNotSent: this.emailNotSent,
      selectedRole: this.selectedRole()!,
      userGroups: this.userGroups() ?? [],
    };

    const dialogRef = this.dialog.open(AdminInvitationDialogComponent, {
      data,
      width: '600px',
    });

    this.subscribeToDialogResult(dialogRef);
  }

  private subscribeToDialogResult(dialogRef: MatDialogRef<AdminInvitationDialogComponent>): void {
    dialogRef.afterClosed().subscribe((result: IAdminUserInvitation | TNil) => {
      this.refreshUserGroups.emit();

      if (!result) {
        return;
      }

      if (this.selectedRole() && this.datatableParams$.value) {
        this.store.fetchInvitations({ params: this.prepareFetchParams(this.datatableParams$.value) });
      }
    });
  }
}
